import { Component, Input, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastService } from "@core/components/toasts/toasts.service";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ConfirmDialogComponent } from "app/layout/components/confirm-dialog/confirm-dialog.component";
import { FormAbstact } from "app/views/pages/abstract/form-abstract";
import moment from "moment-business-days";
import { CustomValidators } from "ng2-validation";
import { AuthService } from "services/auth.service";
import { VacationScheduleService } from "services/corporate/vacationSchedule.service";
import { DomainService } from "services/domain.service";
import { VacationLimitType } from "services/enums/VacationLimitType.enum";
import { VactionScheduleStatusType } from "services/enums/VactionScheduleStatusType.enum";
import { ReferencePeriodVacationModel } from "services/models/corporate/referencePeriodVacation.model";
import { ReferencePeriodVacationScheduleModel } from "services/models/corporate/referencePeriodVacationSchedule.model";
import { VacationManagementParametersModel } from "services/models/vacationManagementParameters.model";
import { FormValidations } from "utils/form-validation";
import { comparer, convertToUpperCase, omit_special_char } from "utils/helps";

@Component({
  selector: "app-create-vacation-schedule-modal",
  templateUrl: "./create-vacation-schedule-modal.component.html",
  styleUrls: ["./create-vacation-schedule-modal.component.scss"],
})
export class CreateVacationScheduleModalComponent
  extends FormAbstact<
    ReferencePeriodVacationScheduleModel,
    VacationScheduleService
  >
  implements OnInit {
  @Input() isSameUser: boolean = false;
  @Input() referencePeriod: ReferencePeriodVacationModel;
  @Input() vacationSchedule: ReferencePeriodVacationScheduleModel = null;
  @Input() onlyView: boolean = false;
  actionText: "Salvar" | "Programar" | "Solicitar Programação" =
    "Solicitar Programação";
  compareValues = comparer;
  convertToUpperCase = convertToUpperCase;
  omitChar = omit_special_char;
  minDate = moment().add(5, "day").format("YYYY-MM-DD");
  vacationManagementParameters: VacationManagementParametersModel;
  hasPrepayment = false;
  hasAllowance = false;
  showJustificationInput = false;
  readonly FIXED_ALLOWANCE_DAYS = 10;
  private _subs: Array<any> = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    public activeModal: NgbActiveModal,
    private _authService: AuthService,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private _modalService: NgbModal,
    private toasterService: ToastService,
    private service: VacationScheduleService,
    private _domain: DomainService
  ) {
    super(service, activeRoute, router, toasterService);
  }

  ngOnInit(): void {
    this.vacationManagementParameters = this._domain.getDomain().vacationManagementParameters;

    this.initializeForm();
    this.initJustificationWatcher();
    this.setActionText();
    if (this.vacationSchedule) {
      this.vacationSchedule.date = moment(this.vacationSchedule.date).format(
        "yyyy-MM-DD"
      );
      this.hasPrepayment = this.vacationSchedule.prepaymentPercent13S > 0;
      this.form.patchValue(this.vacationSchedule);
      this.form.updateValueAndValidity();
      if (this.lockAllowanceDays) {
        this.hasAllowance = this.amountDaysAllowanceField.value > 0;
      }
    }
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      referencePeriodId: [
        this.referencePeriod.id,
        [Validators.required, FormValidations.guidRequired],
      ],
      date: ["", [Validators.required]],
      amountDaysVacation: [
        0,
        [Validators.required, Validators.min(5), Validators.max(30)],
      ],
      amountDaysAllowance: [
        0,
        this.lockAllowanceDays
          ? [Validators.required]
          : [Validators.required, Validators.min(0), Validators.max(30)],
      ],
      prepaymentPercent13S: [0 ,[Validators.min(0), Validators.max(this.vacationManagementParameters.maximumPrepaymentPercent)]],
      allowanceReason: ["", []],
    });

    if (this.onlyView) {
      this.form.disable();
    }

    this.applyScheduleMinDate();
  }

  private applyScheduleMinDate(): void {
    const params = this.vacationManagementParameters;
    if (!params) {
      return;
    }

    if (params.isCollectivePeriod) {
      this.minDate = moment().format("YYYY-MM-DD");
      this.dateField.setValidators([Validators.required, FormValidations.minDateValidator(this.minDate)]);
      this.form.updateValueAndValidity();
      return;
    }

    if (this.service.isVacationManager) {
      return;
    }

    const configuredMinDays = params.minDaysFromTodayToStart || 0;
    const minDays = configuredMinDays > 30 ? configuredMinDays : 30;
    this.minDate = moment().add(minDays, "day").format("YYYY-MM-DD");

    if (
      params.vacationLimitDateType === VacationLimitType.Custom.value &&
      !params.controlAccessByHierarchyLevel &&
      moment().date() > params.vacationCutDate
    ) {
      const cutMinDate = moment().add(2, "month").startOf("month").format("YYYY-MM-DD");
      if (moment(cutMinDate).isAfter(this.minDate, "day")) {
        this.minDate = cutMinDate;
      }
    }

    this.dateField.setValidators([Validators.required, FormValidations.minDateValidator(this.minDate)]);
    this.form.updateValueAndValidity();
  }

  initJustificationWatcher() {
    const sub = this.amountDaysAllowanceField.valueChanges.subscribe(() => {
      this.toggleJustification();
      this.setActionText();
    });
    this.toggleJustification();
    this._subs.push(sub);
  }

  toggleJustification() {
    const raw = this.amountDaysAllowanceField.value;
    const numeric = Number(String(raw).replace(/\D+/g, '')) || 0;
    const shouldShow = numeric > 0;
    this.showJustificationInput = shouldShow;
    const control = this.allowanceReasonField;

    control.setValidators(shouldShow ? [Validators.required] : []);

    if (!shouldShow) {
      control.setValue('');
    }
    control.updateValueAndValidity();
  }

  hasPrepaymentChange(ev: any) {
    this.hasPrepayment = ev.target.checked;
    if (this.hasPrepayment) {
      this.prepaymentPercent13SField.setValue(this.vacationManagementParameters.lockPrepaymentPercent ? this.vacationManagementParameters.maximumPrepaymentPercent : 0);
    } else {
      this.prepaymentPercent13SField.setValue(0);
    }

    this.form.updateValueAndValidity();
  }

  onAllowanceChange(ev: any) {
    this.hasAllowance = ev.target.checked;
    this.amountDaysAllowanceField.setValue(
      this.hasAllowance ? this.FIXED_ALLOWANCE_DAYS : 0
    );
    this.setActionText();
  }

  setActionText() {
    this.actionText = this.vacationSchedule
      ? "Salvar"
      : (this.service.hasAllowancePermission || this.service.isVacationManager) ||
        (this.service.hasWithoutAllowancePermission && !this.amountDaysAllowanceField.value) ||
        ((this.service.isOnlyManager || this.service.isManager) && !this.vacationManagementParameters.controlAccessByHierarchyLevel)
        ? "Programar"
        : "Solicitar Programação";
  }

  onDismiss(): void {
    this.activeModal.close(false);
  }

  handleSubmit() {
    if (this.validaForm()) {
      this.updateObject();
      this.saveObject();
    }
  }

  updateObject() {
    this.object = this.object.updateModel(this.form?.value);
    this.object.id = this.vacationSchedule
      ? this.vacationSchedule.id
      : this.object.id;

    if (this.canChange()) {
      this.object.status = this.vacationSchedule
        ? this.vacationSchedule.status
        : VactionScheduleStatusType.Approved.value;
    }

    if (this.isSameUser && (!this.service.hasWithoutAllowancePermission && !this.service.isVacationManager)) {
      this.object.status = VactionScheduleStatusType.Pending.value;
    }

    // Verifica se não é administrador e é apenas gestor
    const isOnlyManagerAndCustomLimit =
      (!this.service.hasWithoutAllowancePermission && !this.service.isVacationManager) &&
      this.service.isOnlyManager && this.vacationManagementParameters.vacationLimitDateType === VacationLimitType.Custom.value;

    // Verifica se a solicitação é para o mês atual OU se é após exceder a data de corte para o mês atual OU próximo mês
    const creationDate = moment();
    const scheduleDate = moment(this.object.date);
    const isSameMonth = scheduleDate.month() === creationDate.month();
    const isNextMonth =
      scheduleDate.month() === creationDate.add(1, "month").month();
    const hasExceededCutDate =
      creationDate.date() > this.vacationManagementParameters.vacationCutDate;

    if (
      isOnlyManagerAndCustomLimit &&
      (isSameMonth || (hasExceededCutDate && (isSameMonth || isNextMonth)))
    ) {
      this.object.status = VactionScheduleStatusType.Pending.value;
    }

    this.convertToUpperCase(this.object);
  }

  saveObject() {
    this.service.save(this.object.id, this.object).subscribe(() => {
      this.toasterService.showSuccess(`Operação realizada com sucesso!`);
      this.activeModal.close(true);
    });
  }

  openApproveVacationSchedule(status: number) {
    const modalRef = this._modalService.open(ConfirmDialogComponent);
    const action = status == 2 ? "rejeitar" : "aprovar";
    modalRef.componentInstance.message = `Deseja realmente ${action} a programação de férias?`;
    modalRef.componentInstance.focusConfirm = true;
    modalRef.closed.subscribe(() => {
      this.saveApprove(status);
    });
  }

  saveApprove(status: number) {
    this.object = this.object.updateModel(this.vacationSchedule);
    this.object.status = status;
    this.convertToUpperCase(this.object);
    this._service.changeVacationScheduleStatus(this.object).subscribe(
      () => {
        const action = status == 2 ? "rejeitada" : "aprovada";
        this.toasterService.showSuccess(`Programação ${action} com sucesso.`);
        this.activeModal.close(true);
      },
      (error) => {
        this.toasterService.showError(error.message);
      }
    );
  }

  canChange() {
    return this.service.hasAllowancePermission || this.service.hasWithoutAllowancePermission || this.service.isVacationManager 
    || ((this.service.isOnlyManager || this.service.isManager) && !this.vacationManagementParameters.controlAccessByHierarchyLevel);
  }

  get dateField() {
    return this.form.get("date") as UntypedFormControl;
  }
  get amountDaysVacationField() {
    return this.form.get("amountDaysVacation") as UntypedFormControl;
  }
  get amountDaysAllowanceField() {
    return this.form.get("amountDaysAllowance") as UntypedFormControl;
  }
  get prepaymentPercent13SField() {
    return this.form.get("prepaymentPercent13S") as UntypedFormControl;
  }
  get allowanceReasonField() {
    return this.form.get("allowanceReason") as UntypedFormControl;
  }

  get lockAllowanceDays(): boolean {
    return !!this.vacationManagementParameters?.lockAllowanceDays;
  }
}
